import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { formatSliderLabel } from '../Components/LimitSlider/sliderUtils';
import { LimitConfig } from '../types';
import {
  clampAmount,
  getAmountRangeValidationMessage,
} from '../utils';

const NATIVE_THROTTLE_MS = 40;

export function useCreditLimitAmount(
  limitConfig: LimitConfig,
  initialAmount: number,
) {
  const [selectedAmount, setSelectedAmount] = useState(initialAmount);
  const [amountInput, setAmountInput] = useState(String(initialAmount));
  const isEditingInput = useRef(false);

  const isDraggingSlider = useRef(false);
  const lastUpdateTs = useRef(0);
  const pendingAmountRef = useRef<number | null>(null);
  const throttleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (throttleTimerRef.current) clearTimeout(throttleTimerRef.current);
    };
  }, []);

  const applyAmount = useCallback(
    (value: number, syncInput = true) => {
      const clamped = clampAmount(value, limitConfig);
      setSelectedAmount(clamped);
      if (syncInput) {
        setAmountInput(String(clamped));
      }
      return clamped;
    },
    [limitConfig],
  );

  const onSliderAmountChange = useCallback(
    (value: number) => {
      isEditingInput.current = false;
      isDraggingSlider.current = true;
      const clamped = clampAmount(value, limitConfig);

      if (Platform.OS === 'web') {
        setAmountInput(String(clamped));
        setSelectedAmount(clamped);
        return;
      }

      pendingAmountRef.current = clamped;
      const now = Date.now();
      if (now - lastUpdateTs.current >= NATIVE_THROTTLE_MS) {
        setAmountInput(String(clamped));
        setSelectedAmount(clamped);
        lastUpdateTs.current = now;
        pendingAmountRef.current = null;
      } else if (!throttleTimerRef.current) {
        throttleTimerRef.current = setTimeout(() => {
          throttleTimerRef.current = null;
          if (pendingAmountRef.current !== null) {
            setAmountInput(String(pendingAmountRef.current));
            setSelectedAmount(pendingAmountRef.current);
            lastUpdateTs.current = Date.now();
            pendingAmountRef.current = null;
          }
        }, NATIVE_THROTTLE_MS);
      }
    },
    [limitConfig],
  );

  const flushSliderDrag = useCallback(() => {
    isDraggingSlider.current = false;
    if (throttleTimerRef.current) {
      clearTimeout(throttleTimerRef.current);
      throttleTimerRef.current = null;
    }
    if (pendingAmountRef.current !== null) {
      setAmountInput(String(pendingAmountRef.current));
      setSelectedAmount(pendingAmountRef.current);
      pendingAmountRef.current = null;
    }
  }, []);

  const amountValidationError = useMemo(
    () =>
      getAmountRangeValidationMessage(
        amountInput,
        limitConfig,
        (amount) => formatSliderLabel(amount, true),
      ),
    [amountInput, limitConfig],
  );

  useEffect(() => {
    setSelectedAmount((prev) => clampAmount(prev, limitConfig));
  }, [limitConfig]);

  useEffect(() => {
    if (!isEditingInput.current && !isDraggingSlider.current) {
      setAmountInput(String(selectedAmount));
    }
  }, [selectedAmount]);

  return {
    selectedAmount,
    setSelectedAmount,
    onSliderAmountChange,
    flushSliderDrag,
    amountInput,
    setAmountInput,
    amountValidationError,
    applyAmount,
    isEditingInput,
  };
}
